/* eslint-disable import/no-duplicates */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useRouter } from 'next/router';

import { format, subMonths } from 'date-fns';
import en from 'date-fns/locale/en-US';
import ru from 'date-fns/locale/ru';
import { detect } from 'detect-browser';
import isEmpty from 'lodash/isEmpty';
import { GetStaticPaths, GetStaticProps } from 'next';

import useI18n, { getLocalePaths, LocaleTypes } from '@i18n';

import BaseMotivation from '@base/BaseMotivation';
import Card from '@pages/statistics/common/Card';
import Chart from '@pages/statistics/common/Chart';
import Filters from '@pages/statistics/common/Filters';
import Table from '@pages/statistics/common/Table';
import TypeFilter from '@pages/statistics/common/TypeFilter';

import apiHooks, { ICreateUseRequestInfiniteResponse } from '@apiHooks';
import appConfig from '@config/app.json';
import routes from '@config/routes.json';
import { useCommonStat, useDayStat, useUBlockDetection } from '@pages/statistics/common/hooks';
import { getPercent, getSumWithCurrency } from '@utils/formatters/d3Formatters';
import sendGaEvent from 'src/utils/metrics/ga';

import {
    ALL_CURRENCIES,
    ALL_CURRENCIES_WITH_TON,
    CHART_OPTIONS_BY_TYPE,
    chartLineOptions,
    commonChartLineOptions,
    datePeriods,
    dateRanges,
    DEFAULT_FILTERS,
    DEFAULT_ORDERS,
    filtersConversionFuncs,
    firstColumnsInfo,
    HINTS,
    LABELS_INFO_BY_TYPE,
    subsList,
    tableColumnsInfo,
    tableColumnsWithSignups,
    typeFilterOptions,
} from '@pages/statistics/common/config';

import links from '@config/external-links.json';

import {
    addSignsTotal,
    convertFilterIntoParams,
    getPeriodByDates,
    getUniqPlacementsOptions,
    prepareSubsSelectOptions,
} from '@pages/statistics/common/utils';

import {
    IGetCommonStatDataFormatted,
    IGetCommonStatSummaryDataAttributes,
    IGetCountriesDataFormatted,
    IGetCreativesDataFormatted,
    IGetOffersDataFormatted,
    IGetPlacementsDataFormatted,
    IGetStatOffersDataFormatted,
    IGetSubsDataFormatted,
} from '@api/types';
import { getDateRange } from '@components/pages/statistics/common/utils';
import {
    ICommonFilterOption,
    ICommonFilterOptions,
    ICommonFilterValues,
    IOrderOption,
    IRange,
} from '@pages/statistics/common/interfaces';
import { CurrencyTypes, CustomGetStaticPathsResult, CustomGetStaticPropsResult } from 'src/types.d';

// HOCs
import WithRole, { rolesEnum } from '@HOCs/WithRole';
import useFilters from '@hooks/useFilters';
import useLocalStorage from '@hooks/useLocalStorage';

const {
    useCountries,
    useSubs,
    useCommonStatSummary,
    useStatOffers,
    useCreatives,
    useCreativesBySearch,
    usePlacements,
} = apiHooks;

const LOADING_STUBS_CARDS = 4;
const browser = detect();
const locales = { ru, en };

// todo: не понятно зачем делать заглавные буквы в числовых датах
const firstLetterToUpperCase = (string: string[]): string[] => {
    return string.map((date: string): string => date.charAt(0).toUpperCase() + date.slice(1));
};

const CommonStatisticsPage: React.FC = () => {
    const { t, locale } = useI18n('Statistic');
    const { query, isReady } = useRouter();
    const { offerIds: urlOfferId, dateFrom: urlDateFrom, dateTo: urlDateTo } = query;

    const [currencyInStorage, setCurrencyInStorage] = useLocalStorage(
        'StatisticCurrency',
        locale === LocaleTypes.ru ? CurrencyTypes.RUB : CurrencyTypes.USD,
    );

    const { data: countries = [] } = useCountries({
        dependencies: [locale],
    }) as ICreateUseRequestInfiniteResponse<IGetCountriesDataFormatted[]>;

    const subsParams: {
        subFields: string;
    } = { subFields: 'sub,sub2,sub3,sub4,sub5' };

    const { data: subsData = [], isLoading: isLoadingSubs } = useSubs({
        params: subsParams,
        dependencies: [locale],
    }) as ICreateUseRequestInfiniteResponse<IGetSubsDataFormatted[]>;

    const filtersValue: ICommonFilterValues = useMemo(
        () => ({
            ...DEFAULT_FILTERS,
            currency: currencyInStorage,
        }),
        [currencyInStorage],
    );

    const filterOptions = {
        conversionFuncs: filtersConversionFuncs,
        coupleKeys: ['dateFrom', 'dateTo'],
    };
    const [filters, setFilters] = useFilters(filtersValue, filterOptions);
    const [sort, setSort] = useState<IOrderOption>(DEFAULT_ORDERS[DEFAULT_FILTERS.type]);
    const [tableColumns, setTableColumns] = useState([]);
    const [initialDatasetsValue, setInitialDatasetsValue] = useState({ ...chartLineOptions });
    const [creativesSearchValue, setCreativesSearchValue] = useState<string>('');
    // Для таблицы
    const [preparedDateRanges, setPreparedDateRanges] = useState([]);
    // максимальный период 12 мес
    const [minMaxDateRange] = useState({
        minDate: subMonths(new Date(), 12),
        maxDate: new Date(),
    });

    const [datePeriod, setDatePeriod] = useState('');

    const initDatePeriodFromUrl = useCallback(() => {
        if (!urlDateFrom || !urlDateTo) {
            setDatePeriod('week');
            return;
        }

        const period = getPeriodByDates(urlDateFrom as string, urlDateTo as string);

        setDatePeriod(period ?? '');
    }, [urlDateFrom, urlDateTo]);

    useEffect(() => {
        if (!isReady) return;

        initDatePeriodFromUrl();
    }, [isReady, initDatePeriodFromUrl, urlDateFrom, urlDateTo]);

    const values = { ...filters, datePeriod };

    const formatArrayForSelect = ({ id, name }) => ({ value: id, label: name });

    // Получем данные для таблицы
    const {
        data: statData,
        isReachingEnd: isReachingEndStat = true,
        isLoading: isLoadingStat,
        error: statError,
        nextPage: nextPageStat,
    } = useCommonStat({
        filters,
        sort,
    });

    // Получаем данные для графика
    const { data: chartData, isLoading: isLoadingChart, error: chartError } = useCommonStat({
        filters,
        isForChart: true,
    });

    // Получаем список офферов для фильтров
    const { data: statOffersData = [], isLoading: isLoadingOffers } = useStatOffers({
        // список типов/классов офферов (1 = default, 2 = ref, 3 = offline, 4 = offline multi)
        params: { typeIds: '1,2,3,6,7' },
        dependencies: [locale],
    }) as ICreateUseRequestInfiniteResponse<IGetStatOffersDataFormatted[]>;

    const neededOffersIds: string = statOffersData.map(({ id }: { id: number }) => id).join(',');

    const firstOfferValue: ICommonFilterOption = {
        value: 0,
        label: t('All offers'),
    };
    const firstCreativeValue: ICommonFilterOption = {
        value: -1,
        label: t('All creatives'),
    };
    const firstPlacementValue: ICommonFilterOption = {
        value: 0,
        label: t('All placements'),
    };

    const offersSelectOptions: ICommonFilterOption[] = [
        firstOfferValue,
        ...statOffersData.map(formatArrayForSelect),
    ];

    const { offerIds } = filters;

    const creativesParams = {
        name: creativesSearchValue,
        isDeleted: false,
        offersIds: neededOffersIds,
        fields: 'id,description,offerId',
        limit: 20,
        search: creativesSearchValue ?? '',
    };

    const {
        data: creativesData,
        nextPage: nextPageCreatives,
        isLoading: isLoadingRegularCreatives,
        isReachingEnd: isReachingEndCreatives,
    } = useCreatives({
        params: {
            fields: 'id,description,offerId',
            offersIds: neededOffersIds,
            isDeleted: false,
            limit: 20,
        },
        dependencies: [neededOffersIds, locale],
        condition: !!neededOffersIds,
    }) as ICreateUseRequestInfiniteResponse<IGetCreativesDataFormatted[]>;

    const { data: creativesBySearch, isLoading: isLoadingCreativesBySearch } = useCreativesBySearch(
        {
            params: creativesParams,
            dependencies: [creativesSearchValue],
            condition: !!creativesSearchValue,
        },
    ) as ICreateUseRequestInfiniteResponse<IGetCreativesDataFormatted[]>;

    const {
        data: placementsData = [],
        nextPage: nextPagePlacements,
        isLoading: isLoadingPlacements,
        isReachingEnd: isReachingEndPlacements,
    } = usePlacements({
        params: {
            statuses: 'allow',
            offerIds: offerIds || '',
            fields: 'id,url,offer_id',
            limit: 100,
        },
        dependencies: [offerIds],
    }) as ICreateUseRequestInfiniteResponse<IGetPlacementsDataFormatted[]>;

    const filteredCreativesData = creativesData.filter(({ offerId }) =>
        filters.offerIds ? Number(filters.offerIds) === Number(offerId) : offerId,
    );

    const filteredCreativesBySearchData = creativesBySearch.filter(
        ({ offerId }: IGetCreativesDataFormatted) =>
            filters.offerIds ? Number(filters.offerIds) === Number(offerId) : offerId,
    );

    const filtersDependencies = Object.values(filters).map(value =>
        Array.isArray(value) ? value.toString() : value,
    );

    const hasUblock = useUBlockDetection();
    // Данные для строки ВСЕГО

    const {
        data: summaryData,
        isLoading: commonStatsSummaryIsLoading,
        error: summaryError,
    } = useCommonStatSummary({
        condition: !hasUblock,
        params: convertFilterIntoParams(filters),
        dependencies: [...filtersDependencies],
    }) as ICreateUseRequestInfiniteResponse<IGetCommonStatSummaryDataAttributes>;

    /**
     * Готовит массив валют для фильтра валют, исходя из того, какой оффер выбран на данный момент
     */
    const getCurrencies = useCallback(() => {
        const offerCurrencies =
            statOffersData.find(offer => Number(offer.id) === Number(filters.offerIds))?.currency ||
            [];

        return offerCurrencies?.map(currency => ({
            label: currency,
            value: currency,
        }));
    }, [statOffersData, filters.offerIds]);

    /**
     * Получает все возможные валюты из списка офферов.
     */
    const getCurrenciesWithTon = useCallback(():
        | typeof ALL_CURRENCIES_WITH_TON
        | typeof ALL_CURRENCIES => {
        const findTon = statOffersData.find(offer => offer.currency.includes(CurrencyTypes.TON));
        return findTon ? ALL_CURRENCIES_WITH_TON : ALL_CURRENCIES;
    }, [statOffersData]);

    const currencies: ICommonFilterOption[] = useMemo(
        () => (filters.offerIds === 0 ? getCurrenciesWithTon() : getCurrencies()),
        [filters.offerIds, getCurrenciesWithTon, getCurrencies],
    );

    // Если при переключении оффера у него отсутсвует текущая валюта, то ставим его первую возможную валюту
    useEffect((): void => {
        const isOfferIdAndCurrenciesExist = Boolean(offerIds && currencies.length);

        if (isOfferIdAndCurrenciesExist) {
            const currenOfferCurrencies: CurrencyTypes[] =
                statOffersData.find(item => item.id === offerIds)?.currency || [];
            const isValidCurrency: boolean = currenOfferCurrencies.includes(filters.currency);

            // Check for locale currency. If currencies list has not contain currency from param query or locale
            const currencyByLocale =
                locale === LocaleTypes.ru ? CurrencyTypes.RUB : CurrencyTypes.USD;
            const isValidCurrencyByLocale =
                currenOfferCurrencies.includes(currencyByLocale) || null;

            if (!isValidCurrency && isValidCurrencyByLocale) {
                setFilters(filterValues => ({
                    ...filterValues,
                    currency:
                        currencyInStorage ??
                        currenOfferCurrencies.find(cur => cur === currencyByLocale),
                }));
                return;
            }

            if (!isValidCurrency && !isValidCurrencyByLocale) {
                setFilters(filterValues => ({
                    ...filterValues,
                    currency: currenOfferCurrencies[0],
                }));
            }
        }
        // eslint-disable-next-line
    }, [offerIds]);

    const creativesSelectOptions: ICommonFilterOption[] = [
        firstCreativeValue,
        ...filteredCreativesBySearchData.map(formatArrayForSelect),
        ...filteredCreativesData.map(formatArrayForSelect),
    ];

    const subsSelectOptions = prepareSubsSelectOptions(subsData);

    const placementsSelectOptions: ICommonFilterOption[] = [
        firstPlacementValue,
        // схлопываем одинаковые урлы площадок
        ...getUniqPlacementsOptions(placementsData),
    ];

    const options: ICommonFilterOptions = {
        offers: offersSelectOptions,
        creatives: creativesSelectOptions,
        placements: placementsSelectOptions,
        dateRanges: preparedDateRanges,
        subs: subsSelectOptions,
        currencies,
    };

    const loadings = {
        isLoadingOffers,
        isLoadingCreatives: isLoadingCreativesBySearch || isLoadingRegularCreatives,
        isLoadingPlacements,
        isLoadingSubs,
    };

    const dataWithStats: {
        offer: IGetOffersDataFormatted[];
        creative: IGetCreativesDataFormatted[];
    } = {
        offer: statOffersData,
        creative: [...filteredCreativesData, ...filteredCreativesBySearchData],
    };

    const { type, currency } = filters;

    const preparedTotal = useMemo(() => {
        const isRef: boolean = statOffersData.find(
            (item: IGetStatOffersDataFormatted) => item.id === filters.offerIds,
        )?.ref;

        const newTotal: IGetCommonStatSummaryDataAttributes = { ...summaryData };

        // TODO: когда не реферальный оффер нужно убрать этот столбец из таблицы
        if (!isRef) {
            delete newTotal.signups;
        }

        return addSignsTotal(newTotal, currency);
        // eslint-disable-next-line
    }, [currency, summaryData]);

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const getNameById = (id: string | number, type: string): string =>
        dataWithStats?.[type]?.find(({ id: localId }): boolean => Number(localId) === Number(id))
            ?.name || `${id}`;

    const getCountryName = (countryCode: string): string =>
        countries?.find(
            ({ value }: IGetCountriesDataFormatted): boolean => value.toLowerCase() === countryCode,
        )?.label || countryCode;

    const getPlacementUrl = (placementId: string): string =>
        placementsData.find(({ id }: IGetPlacementsDataFormatted): boolean => id === placementId)
            ?.url || placementId;

    // Готовим дланные для таблицы
    const tableData = useMemo(
        () =>
            statData.reduce((data, stat: IGetCommonStatDataFormatted) => {
                const {
                    date: unformattedDate,
                    os: osValue,
                    material: materialValue,
                    countryCode: countryValue,
                    placementId: placementValue,
                    sub,
                    sub2,
                    sub3,
                    sub4,
                    sub5,
                    offerId,
                    creativeId,
                    creativeName,
                    clicks,
                    hosts,
                    cr,
                    turnover,
                    epc,
                    leadsHold,
                    leads,
                    revenueHold,
                    revenue,
                    signups,
                } = stat;

                let date: string | null = null;
                // т.к клик по дате работает по ключу date, надо завести отдельный ключ для статы по часам
                let dateTime: string | null = null;
                let month: string | null = null;
                let os: string = osValue;
                let material: string = materialValue;
                let countryCode: string = countryValue;
                let placementId: string = placementValue;

                const validDateForSafari: string =
                    unformattedDate && unformattedDate.replace(/-/g, '/');

                if (unformattedDate) {
                    if (type === 'hour') {
                        dateTime = format(new Date(validDateForSafari), 'dd MMM HH:mm');
                    } else if (type === 'month') {
                        month = format(new Date(validDateForSafari), 'MMM yyyy');
                    } else {
                        date = format(new Date(validDateForSafari), 'dd MMM yyyy');
                    }
                }

                // Хаки, потому что с бэка не определенные значения приходят пустой строкой
                if (os === '') os = t('Undefined');
                if (material === '') material = t('Undefined');
                if (countryCode === '') countryCode = t('Undefined');
                if (placementId === '') placementId = t('Undefined');

                const isRef: boolean = statOffersData.find(
                    (item: IGetStatOffersDataFormatted): boolean => item.id === filters.offerIds,
                )?.ref;

                data.push({
                    // Вместе с sub приходит date
                    ...(date && { date }),
                    ...(dateTime && { dateTime }),
                    ...(month && { month }),
                    // Показываем поле sub только если фильтр type один из sub'ов
                    ...(subsList.includes(type) && sub && { sub }),
                    ...(subsList.includes(type) && sub2 && { sub2 }),
                    ...(subsList.includes(type) && sub3 && { sub3 }),
                    ...(subsList.includes(type) && sub4 && { sub4 }),
                    ...(subsList.includes(type) && sub5 && { sub5 }),
                    ...(offerId && { offerName: getNameById(offerId, type) }),
                    ...(creativeId !== undefined && {
                        creativeName: creativeName || getNameById(creativeId, type),
                    }),
                    ...(countryCode && { countryCode: getCountryName(countryCode) }),
                    ...(os && { os }),
                    ...(material && { material }),
                    ...(placementId && { placement: getPlacementUrl(placementId) }),
                    clicks,
                    hosts,
                    // TODO: когда не реферальный оффер нужно убрать этот столбец из таблицы
                    ...(isRef && { signups }),
                    cr: getPercent(cr),
                    epc: getSumWithCurrency(epc, currency),
                    turnover: getSumWithCurrency(turnover, currency),
                    leadsHold,
                    leads,
                    revenueHold: getSumWithCurrency(revenueHold, currency),
                    revenue: getSumWithCurrency(revenue, currency),
                });

                return data;
            }, []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [statData],
    );

    const onDateRangeChange = useCallback(
        (dateType: string, range?: IRange) => {
            const { dateFrom, dateTo } = datePeriods[dateType] || range;

            setFilters({ ...filters, dateFrom, dateTo });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filters],
    );

    /**
     * Фильтры type и sub взаимоисключающие
     */
    const filtersOnChange = useCallback(
        (name, value) => {
            // Для случая нажатия backspace
            if (!value && typeof value !== 'number') {
                return false;
            }
            const { dateFrom, dateTo, value: selectValue } = value;
            const isRadioButton = typeof value === 'string';
            let newValue = null;
            let fieldName = name;

            if (name === 'type') {
                sendGaEvent({
                    eventCategory: 'statistics',
                    eventAction: 'open_tab',
                    eventLabel: value,
                });
            }
            // todo: add check for manual filter
            if (name === 'currency') setCurrencyInStorage(value);

            if (name === 'dateRange') {
                newValue = dateFrom || dateTo;
                fieldName = dateFrom ? 'dateFrom' : 'dateTo';
                // Сбрасываем сортировку при изменении фильтра
                setSort(DEFAULT_ORDERS[type]);
                return setFilters({ ...filters, ...{ dateFrom, dateTo } });
            }

            if (selectValue) newValue = selectValue;

            if (isRadioButton) newValue = value;

            let resultValue = { ...filters, ...{ [fieldName]: newValue } };

            // Сбрасываем сортировку при изменении фильтра
            setSort(DEFAULT_ORDERS[type]);

            // Если фильтр type, ставим сортировку по умолчанию
            if (name === 'type') {
                setSort(DEFAULT_ORDERS[value]);
            }

            // TODO: когда реферальный оффер нужно добавить столбец "Регистрации". Скорее всего можно лучше
            if (name === 'offerIds') {
                const firstTableColumnInfo = firstColumnsInfo[type];

                const isRef = statOffersData.find(item => item.id === value)?.ref;

                const tableRefColumns = isRef
                    ? [firstTableColumnInfo, ...tableColumnsWithSignups]
                    : [firstTableColumnInfo, ...tableColumnsInfo];
                const withLang = tableRefColumns.map(item => ({
                    ...item,
                    key: t(item.key),
                }));

                resultValue = {
                    ...resultValue,
                    creativeIds: '',
                    offerIds: value,
                };
                setTableColumns(withLang);
            }

            // При смене креатива ставить оффер соответсвующий креативу. Если выбраны все офферы, то оставлять фильтр офферов как есть
            if (name === 'creativeIds') {
                // if (creativesSearchValue !== '') {
                //     setCreativesSearchValue('');
                // }
                const { offerId } =
                    filteredCreativesData.find(({ id }) => Number(id) === Number(selectValue)) ||
                    filteredCreativesBySearchData.find(
                        ({ id }) => Number(id) === Number(selectValue),
                    ) ||
                    {};
                resultValue = {
                    ...resultValue,
                    ...(offerId && { offerIds: Number(offerId) || offerId }),
                };
            }

            // обновляем айдишики для площадки
            if (name === 'placement') {
                resultValue = {
                    ...resultValue,
                    ...{ placementIds: value.ids },
                };
            }

            return setFilters(resultValue);
        },
        // eslint-disable-next-line
        [filteredCreativesData, filteredCreativesBySearchData, filters, type, locale],
    );

    /**
     * Функция для поиска креатива по введеннным в селектор значениям
     */
    const onSearchCreatives = (searchValue: string): void => {
        const listOfCreatives = options.creatives.filter(creative =>
            creative.label.toLowerCase().includes(searchValue.toLowerCase()),
        );

        if (listOfCreatives.length > 0) return;

        if (listOfCreatives.length === 0 && searchValue.length > 2) {
            setCreativesSearchValue(searchValue);
        }
    };

    const isBrowser = typeof window !== 'undefined';
    // Форматируем данные для графика
    const prepareDatasets = useCallback(
        datasets => {
            if (isBrowser) {
                const initialDatasetsValueClone = JSON.parse(JSON.stringify(initialDatasetsValue));
                // Список задизеибленых значении графикка
                const disabledChartLegends = JSON.parse(
                    localStorage.getItem('disabledChartLegends') || '[]',
                );
                const dataObj = datasets.reduce((chartObj, dataset) => {
                    // clicks, hosts, leads...
                    const datasetKeys = Object.keys(dataset);
                    const allowedKeys = Object.keys(chartLineOptions);

                    datasetKeys.forEach(key => {
                        if (allowedKeys.includes(key)) {
                            const chartType =
                                chartLineOptions[key].type || CHART_OPTIONS_BY_TYPE[type].type;
                            // eslint-disable-next-line
                            chartObj[key].hidden = disabledChartLegends.includes(key);
                            // eslint-disable-next-line
                            chartObj[key].type = chartType;
                            chartObj[key].data.push(dataset[key]);
                        }
                    });

                    return chartObj;
                }, initialDatasetsValueClone);

                return Object.values(dataObj);
            }

            return [];
        },
        [initialDatasetsValue, type, isBrowser],
    );

    const datasets = useMemo(() => prepareDatasets(chartData), [chartData, prepareDatasets]);

    // xs:py-2
    // Лейблы для графика
    const getLabelsByType = useCallback(
        typeArg => {
            const {
                propertyName,
                propertyName2,
                isNeedMapping,
                isDate,
                formatString,
            } = LABELS_INFO_BY_TYPE[typeArg];
            // Собираем значения для оси x
            const labelsValue =
                chartData.map(item => {
                    const label =
                        propertyName === 'countryCode'
                            ? getCountryName(item[propertyName])
                            : item[propertyName];
                    return label || item[propertyName2] || t('Undefined');
                }) || [];

            // Если это id'шники, проставим названия
            if (isNeedMapping) {
                return labelsValue.map(labelOfferId => {
                    const { name = ' ' } =
                        dataWithStats[typeArg].find(
                            ({ id }) => Number(id) === Number(labelOfferId),
                        ) || {};
                    return name;
                });
            }
            if (isDate) {
                // Данные могут не успеть обновится и чтобы не пытался превратить в Date рандомную строку
                try {
                    const dateString = labelsValue.map(date =>
                        format(new Date(date), formatString, { locale: locales[locale] }),
                    );
                    return firstLetterToUpperCase(dateString);
                } catch {
                    return labelsValue;
                }
            }
            return labelsValue;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [chartData],
    );

    const labels = useMemo(() => getLabelsByType(type), [getLabelsByType, type]);

    const dataIsEmpty = isEmpty(labels) || isEmpty(datasets);

    const getLinkWithParams = (date, dateKey) => {
        const transactionUrl = routes.statistics.transactions;

        const { dateFrom, dateTo } = getDateRange(date, dateKey);

        let { offerIds: offerId } = filters;

        const {
            creativeIds: creativeId,
            currency: currencyFilter,
            sub,
            sub2,
            sub3,
            sub4,
            sub5,
        } = filters;

        if (offerIds === null) {
            offerId = statOffersData.map(offer => offer.id).join(',');
        }
        // берем первый установленный саб
        const currentSub = sub || sub2 || sub3 || sub4 || sub5;

        const subId =
            currentSub &&
            subsData.find((subItem: IGetSubsDataFormatted) =>
                Object.values(subItem.subs).includes(currentSub),
            );

        const params = {
            creativeId,
            currency: currencyFilter,
            subId: subId?.id ?? null,
            tsFrom: format(dateFrom, 'MM-dd-yyyy'),
            tsTo: format(dateTo, 'MM-dd-yyyy'),
            ...(offerId && { offerId }),
        };

        const preparedParams = Object.entries(params)
            .filter(([key]) => !!params[key] && key !== 'dateTo' && key !== 'dateFrom')
            .map(([key, value]) => (value ? `${key}=${value}` : ''))
            .join('&');

        return `${transactionUrl}?${preparedParams}`;
    };

    useEffect(() => {
        // глубокое копирование объекта
        const newDatasetInitialValue = JSON.parse(JSON.stringify(chartLineOptions));
        Object.keys(newDatasetInitialValue).forEach(key => {
            // Добавим переводы
            newDatasetInitialValue[key].label = t(chartLineOptions[key].label);
            // общие стили линии графика
            newDatasetInitialValue[key] = {
                ...newDatasetInitialValue[key],
                ...commonChartLineOptions,
            };
        });
        setInitialDatasetsValue({ ...newDatasetInitialValue });

        setPreparedDateRanges(
            dateRanges.map(dateRange => ({
                ...dateRange,
                label: t(dateRange.key),
            })),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locale]);

    useEffect((): void => {
        // Название и значение для сортировки первой колонки меняется в зависимости от типа(date, offer, creative)
        const firstTableColumnInfo = firstColumnsInfo[type];

        const isRef =
            statOffersData.find(item => item.id === filters.offerIds)?.ref ||
            appConfig.REF_OFFERS_IDS.includes(+urlOfferId);

        const columns = [
            firstTableColumnInfo,
            // // TODO: если реферальный оффер нужно добавить столбец в таблицу
            ...(isRef ? tableColumnsWithSignups : tableColumnsInfo),
        ].map(column => {
            const hintText = HINTS[isRef ? 'REF' : 'DEFAULT'][column.order];

            return {
                ...column,
                key: t(column.key),
                hint: hintText ? t(hintText) : null,
            };
        });

        setTableColumns(columns);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, locale, urlOfferId]);

    const { cards, error: cardsError, isFetching: isFetchingCards } = useDayStat(currency);

    const totalTextNode = hasUblock ? (
        <p>
            {t('Failed to load summary statistics')}.&nbsp;
            {t('Disable your ad blocker')}&nbsp;
            {links.uBlock[browser.name] ? (
                <a
                    target="_blank"
                    className="underline"
                    href={links.uBlock[browser.name]}
                    rel="noreferrer"
                >
                    Ublock Origin
                </a>
            ) : (
                <span>Ublock Origin</span>
            )}
            &nbsp;
            {t('and refresh the page')}.
        </p>
    ) : null;

    return (
        <>
            <BaseMotivation />
            <div className="px-2 -mx-2 overflow-scroll my-[31px] hide-scrollbar">
                {isFetchingCards && (
                    <div className="flex w-full space-x-4 sm:space-x-8 xs:justify-between">
                        {[...new Array(LOADING_STUBS_CARDS)].map((_, index) => (
                            <div
                                key={index}
                                className="w-full h-24 xs:h-[105px] md:h-[119px] lg:h-32 hack-min-w-104px bg-blueGray-300 rounded-2 animate-pulse"
                            />
                        ))}
                    </div>
                )}
                {!isFetchingCards && !cardsError && (
                    <div className="flex w-full gap-[16px] xs:justify-between">
                        {cards.map((card, index) => (
                            <Card key={index} card={card} />
                        ))}
                    </div>
                )}
                {cardsError && (
                    <p className="mt-12 mb-12 text-center">
                        {t('Failed to load daily statistics')}
                        <br />
                        <b>{cardsError}</b>
                    </p>
                )}
            </div>
            <h3 className="font-medium mb-[32px] text-h2 sm:text-h1">{t('General statistic')}</h3>
            <Filters
                values={values}
                options={options}
                loadings={loadings}
                onChange={filtersOnChange}
                onDateRangeChange={onDateRangeChange}
                onSearch={onSearchCreatives}
                minMaxDateRange={minMaxDateRange}
                nextPageCreatives={() =>
                    !isReachingEndCreatives &&
                    (!isLoadingRegularCreatives || isLoadingCreativesBySearch) &&
                    nextPageCreatives()
                }
                nextPagePlacements={() =>
                    !isReachingEndPlacements && !isLoadingPlacements && nextPagePlacements()
                }
            />
            <TypeFilter
                value={type}
                typesList={typeFilterOptions}
                onClick={(value: string) => filtersOnChange('type', value)}
            />

            {!chartError &&
                !isLoadingChart &&
                (dataIsEmpty ? (
                    <div className="my-20 leading-normal text-center">{t('No data available')}</div>
                ) : (
                    <Chart datasets={datasets} labels={labels} />
                ))}

            {statError && (
                <p className="mt-12 mb-12 text-center">
                    {t('Failed to load statistics')}
                    <br />
                    <b>{statError.message}</b>
                </p>
            )}
            <Table
                data={tableData}
                columns={tableColumns}
                total={hasUblock ? null : preparedTotal}
                totalError={hasUblock ? null : summaryError}
                totalTextNode={totalTextNode}
                onSort={params => setSort(params)}
                orderBy={sort}
                onNextPage={() => nextPageStat()}
                isLoadedAll={isReachingEndStat}
                isFetching={isLoadingStat || isLoadingChart || commonStatsSummaryIsLoading}
                getLinkWithParams={getLinkWithParams}
            />
        </>
    );
};

export const getStaticProps: GetStaticProps = async ({
    params,
}): Promise<CustomGetStaticPropsResult> => {
    return { props: { locale: params?.locale } };
};

export const getStaticPaths: GetStaticPaths = async (): Promise<CustomGetStaticPathsResult> => {
    return { paths: getLocalePaths(), fallback: false };
};

export default WithRole(CommonStatisticsPage, [rolesEnum.user]);
